import React from 'react';
import { Badge } from '@parsina/framework';

import { useTranslation } from '../../i18n';
import { EMPTY } from '../../lib/format';
import { num, visitToman } from '../../lib/aiPricingView';
import { ParsehCell, TomanCell, UsdCell, useEnumLabel } from './shared';

/**
 * The money cells of the «ویزیت‌ها» view (PM-0427).
 *
 * The visit RPCs answer in پارسه (earned) and USD (paid) and echo the rate of
 * the policy they were asked about (`AiVisitMoney`). Toman is that rate applied
 * once, in `visitToman` — and only where the credit is SOLD in toman (IRT). A
 * cell therefore reads toman first with its own unit underneath, so a page
 * viewed with no policy still shows what the visit earned and cost, and a
 * deployment whose credit is not priced in toman (PM-0211) shows no toman at
 * all rather than a figure with no rate behind it.
 */

/** `visitToman(money)` for the answer in hand, stable across renders. */
export function useVisitMoney(money) {
  return React.useMemo(() => visitToman(money), [money]);
}

/** The unit a view's EARNED column is headed with. */
export function useEarnedUnit(conv) {
  const { t } = useTranslation();
  return conv.irt ? t('common.units.toman') : t('common.units.parseh');
}

/** The unit a view's PAID column is headed with. */
export function usePaidUnit(conv) {
  const { t } = useTranslation();
  return conv.irt ? t('common.units.toman') : t('common.units.usd');
}

/**
 * What a visit (or a feature, per visit) EARNED: toman at the policy's Parseh
 * value, the پارسه underneath. Without toman, the پارسه is the figure.
 */
export function EarnedCell({ parseh, conv, 'data-testid': testId }) {
  const { t } = useTranslation();
  if (!conv.irt) return <ParsehCell value={parseh} />;
  return (
    <span className="inline-flex flex-col items-end gap-0.5" data-testid={testId}>
      <TomanCell value={conv.fromParseh(parseh)} className="font-semibold" />
      <span className="whitespace-nowrap text-[11px] text-muted-foreground">
        <ParsehCell value={parseh} /> {num(parseh) == null ? null : t('common.units.parseh')}
      </span>
    </span>
  );
}

/**
 * What a visit (or a feature, per visit) PAID its providers: toman at the
 * policy's dollar rate, the USD underneath. `partial` — some of its runs had
 * no price — marks the figure as a floor («حداقل»), because a sum over the
 * priced runs only is less than the truth, never equal to it.
 */
export function PaidCell({ usd, conv, partial, 'data-testid': testId }) {
  const { t } = useTranslation();
  const floor = partial ? (
    <Badge variant="warning" title={t('aiPricing.visits.partialHint')}>{t('aiPricing.visits.partial')}</Badge>
  ) : null;
  if (!conv.irt) {
    return (
      <span className="inline-flex flex-col items-end gap-0.5" data-testid={testId}>
        <UsdCell value={usd} />
        {floor}
      </span>
    );
  }
  return (
    <span className="inline-flex flex-col items-end gap-0.5" data-testid={testId}>
      <TomanCell value={conv.fromUsd(usd)} className="font-semibold" />
      <span className="whitespace-nowrap text-[11px] text-muted-foreground">
        <UsdCell value={usd} /> {num(usd) == null ? null : t('common.units.usd')}
      </span>
      {floor}
    </span>
  );
}

/** A 0..1 share as a percentage in the reader's digits; NULL → «—». */
export function ShareCell({ value, className = '' }) {
  const { formatNumber } = useTranslation();
  const n = num(value);
  return (
    <span className={`tabular-nums ${className}`}>
      {n == null ? EMPTY : formatNumber(n, { style: 'percent', maximumFractionDigits: 0 })}
    </span>
  );
}

/** A per-visit average of a count («۲٫۴»); NULL → «—». */
export function RateCell({ value, className = '' }) {
  const { formatNumber } = useTranslation();
  const n = num(value);
  return (
    <span className={`tabular-nums ${className}`}>
      {n == null ? EMPTY : formatNumber(n, { maximumFractionDigits: 2 })}
    </span>
  );
}

/**
 * The line that says which rate converted the toman on screen — a toman figure
 * with an unstated rate is a number nobody can check (as `ai-services-rate`).
 */
export function RateLine({ money, conv, 'data-testid': testId }) {
  const { t, formatDigits } = useTranslation();
  const statusLabel = useEnumLabel('aiPricing.policies.status');
  if (!conv.irt) return null;
  if (!money?.policyId || !conv.hasRate) {
    return <span data-testid={testId} data-rate="none">{t('aiPricing.visits.noRate')}</span>;
  }
  return (
    <span data-testid={testId} data-rate={money.policyId}>
      {t('aiPricing.visits.atRate', {
        name: money.policyName || formatDigits(money.policyId),
        status: statusLabel(money.policyStatus),
      })}
    </span>
  );
}
